import {
    Directive, TemplateRef, ViewContainerRef, inject, OnInit,
    DestroyRef,
    ChangeDetectorRef,
    ContentChild
} from "@angular/core";
import { FormListComponent } from "../../shared/form-list/form-list.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormListItemDirective } from "../form-list-item/form-list-item.directive";

/**
 * Structural directive that renders a template for each item in a FormListComponent.
 * Subscribes to the parent FormListComponent's items$ observable and creates an embedded view
 * for each item in the list.
 *
 * @example
 * <mgd-form-list formControlName="addresses">
 *   <mat-list-item mgdFormListItem="item" *mgdFormListData="let address; let i = index">
 *     <span>{{i}}: {{address.street}} - {{address.city}}</span>
 *   </mat-list-item>
 * </mgd-form-list>
 */
@Directive({
    selector: "[mgdFormListData]"
})
export class FormListDataDirective implements OnInit {

    @ContentChild(FormListItemDirective) formListItem: FormListItemDirective;

    private readonly _formList = inject(FormListComponent, { host: true });

    private readonly _templateRef = inject(TemplateRef);
    private readonly _viewContainerRef = inject(ViewContainerRef);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _cdRef = inject(ChangeDetectorRef);

    async ngOnInit(): Promise<void> {
        this._formList.items$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(
                (data: Array<Record<string, unknown>>) => {
                    this._viewContainerRef.clear();
                    data.forEach((item: Record<string, unknown>, index: number) => {
                        this._viewContainerRef.createEmbeddedView(this._templateRef, {
                            // Create the embedded view with a context object that contains
                            // the data via the key `$implicit`.
                            $implicit: item,
                            index: index
                        }, {
                            index: index
                        });
                    });
                    this._cdRef.detectChanges();
                }
            );
    }
}
